import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import './SuperAdminCompaniesPage.css';

function SuperAdminCompaniesPage() {
  const navigate = useNavigate();
  const { user, selectCompany, refreshCompanies } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: pageSize,
        offset: page * pageSize,
      };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;

      const result = await DashboardApi.getAdminCompanies(params);
      setCompanies(result.companies || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message || 'Kunne ikke hente selskaper');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    loadCompanies();
  }, [loadCompanies, user, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const handleEnterCompany = async (company) => {
    try {
      // Select this company in the context
      await selectCompany(company.id);
      await refreshCompanies();
      // Navigate to dashboard
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Kunne ikke gå inn i selskapet');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    return date.toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'disabled':
        return 'Deaktivert';
      case 'archived':
        return 'Arkivert';
      default:
        return status || '—';
    }
  };

  const statusClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'disabled':
        return 'status-disabled';
      case 'archived':
        return 'status-archived';
      default:
        return '';
    }
  };

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <div className="super-admin-companies">
      <header className="page-header">
        <div className="page-header-content">
          <span className="page-badge">Super Admin</span>
          <h1>Alle selskaper</h1>
          <p className="page-subtitle">
            Søk, filtrer og administrer alle selskaper i systemet
          </p>
        </div>
        <div className="page-header-stats">
          <div className="stat-card">
            <span className="stat-value">{total}</span>
            <span className="stat-label">Totalt</span>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button type="button" onClick={() => setError(null)}>
            Lukk
          </button>
        </div>
      )}

      <div className="filters-section">
        <form className="search-form" onSubmit={handleSearch}>
          <input
            type="search"
            placeholder="Søk etter selskapsnavn..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Søk
          </button>
        </form>
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            className="filter-select"
          >
            <option value="">Alle</option>
            <option value="active">Aktiv</option>
            <option value="disabled">Deaktivert</option>
            <option value="archived">Arkivert</option>
          </select>
        </div>
      </div>

      <div className="companies-table-container">
        <table className="companies-table">
          <thead>
            <tr>
              <th>Selskap</th>
              <th>Eier</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Bots</th>
              <th>Medlemmer</th>
              <th>Opprettet</th>
              <th>Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {loading && companies.length === 0 && (
              <tr>
                <td colSpan="8" className="loading-cell">
                  Laster selskaper...
                </td>
              </tr>
            )}
            {!loading && companies.length === 0 && (
              <tr>
                <td colSpan="8" className="empty-cell">
                  Ingen selskaper funnet
                </td>
              </tr>
            )}
            {companies.map((company) => (
              <tr key={company.id}>
                <td className="company-cell">
                  <strong>{company.name}</strong>
                  {company.slug && (
                    <span className="company-slug">/{company.slug}</span>
                  )}
                </td>
                <td className="owner-cell">
                  <span className="owner-name">{company.ownerName || '—'}</span>
                  {company.ownerEmail && (
                    <span className="owner-email">{company.ownerEmail}</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${statusClass(company.status)}`}>
                    {statusLabel(company.status)}
                  </span>
                </td>
                <td>
                  <span className="plan-badge">{company.planTier || 'free'}</span>
                </td>
                <td className="number-cell">{company.botCount || 0}</td>
                <td className="number-cell">{company.memberCount || 0}</td>
                <td className="date-cell">{formatDate(company.createdAt)}</td>
                <td className="actions-cell">
                  <button
                    type="button"
                    className="action-button primary"
                    onClick={() => handleEnterCompany(company)}
                  >
                    Gå inn
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            type="button"
            className="pagination-button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Forrige
          </button>
          <span className="pagination-info">
            Side {page + 1} av {totalPages}
          </span>
          <button
            type="button"
            className="pagination-button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Neste →
          </button>
        </div>
      )}
    </div>
  );
}

export default SuperAdminCompaniesPage;

